import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const VideoCard = ({ video, index, playLabel, onPlay }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.9, delay: 0.1 + index * 0.12, ease }}
    className="relative"
  >
    <div className="absolute inset-0 border border-volt/50 translate-x-3 translate-y-3 pointer-events-none" />
    <button
      type="button"
      onClick={onPlay}
      data-testid={`showreel-play-button-${index}`}
      aria-label={`${playLabel} — ${video.label}`}
      className="relative block w-full aspect-video bg-graphite border border-line overflow-hidden group cursor-pointer text-left"
    >
      <img
        src={video.poster}
        alt={video.label}
        className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
      />
      <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/20 transition-colors duration-500" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex items-center gap-3 bg-volt text-ink font-mono text-xs uppercase tracking-[0.25em] px-5 py-3.5 group-hover:bg-paper transition-colors duration-300">
          <Play size={14} fill="currentColor" />
          {playLabel}
        </span>
      </div>
      <div className="absolute top-0 left-0 bg-ink/80 backdrop-blur-sm border-r border-b border-line px-3 py-2 font-mono text-[10px] tracking-[0.25em] text-volt">
        /{String(index + 1).padStart(2, "0")}
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-ink/80 backdrop-blur-sm border-t border-line px-4 py-3 flex justify-between items-center gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[0.25em] text-paper">{video.label}</div>
          <div className="font-mono text-[9px] tracking-[0.15em] text-dim mt-0.5 truncate uppercase">{video.sub}</div>
        </div>
        <span className="font-mono text-[10px] tracking-[0.2em] text-volt whitespace-nowrap">{video.note}</span>
      </div>
    </button>
  </motion.div>
);

export const Showreel = () => {
  const { t } = useLang();
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  return (
    <section id="showreel" data-testid="showreel-section" className="py-24 md:py-32 px-6 lg:px-10 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease }}
        className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10"
      >
        <div className="lg:col-span-7">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-volt">{t.showreel.overline}</span>
          <h2 className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl text-paper mt-4 leading-tight">
            {t.showreel.title}
          </h2>
        </div>
        <p className="lg:col-span-5 font-body text-sm md:text-base text-dim leading-relaxed">{t.showreel.body}</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        {t.showreel.videos.map((v, i) => (
          <VideoCard key={v.src} video={v} index={i} playLabel={t.showreel.play} onPlay={() => setActive(i)} />
        ))}
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="showreel-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
            onClick={() => setActive(null)}
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              data-testid="showreel-close-button"
              aria-label="Close"
              className="absolute top-5 right-5 md:top-8 md:right-8 text-dim hover:text-volt p-2 z-10"
            >
              <X size={28} />
            </button>
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.4, ease }}
              className="relative w-full max-w-6xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute inset-0 border border-volt/50 translate-x-3 translate-y-3 pointer-events-none" />
              <video
                data-testid="showreel-video"
                src={t.showreel.videos[active].src}
                poster={t.showreel.videos[active].poster}
                controls
                autoPlay
                playsInline
                className="relative w-full aspect-video bg-ink border border-line"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
